import {AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import {ChatService} from "../../services/chat/chat.service";
import { PerformanceMetricsService } from "../../services/performance-metrics.service"; // Add this
import { ErrorTrackingService } from "../../services/error-tracking.service"; // Add this
import { trace, context } from '@opentelemetry/api';

@Component({
    selector: 'app-prompt',
    templateUrl: './prompt.component.html',
    styleUrls: ['./prompt.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager
})
export class PromptComponent implements AfterViewInit {
  @ViewChild('myTextarea') myTextarea!: ElementRef;

  @Output() onSendMessage = new EventEmitter<string>();
  public promptText: string = '';

  // Whether messages can be sent (controlled by conversation state)
  @Input() canSend: boolean = true;

  private initialHeight: number = 44; // Compact height like WhatsApp (44-20=24px textarea)
  private firstInteractionTracked = false;

  constructor(
    private chatService: ChatService,
    private performanceMetrics: PerformanceMetricsService,
    private errorTracking: ErrorTrackingService
  ) {}

  sendChatMessage(): void {
    try {
      // Check if sending is allowed (conversation state)
      if (!this.canSend) {
        console.warn('[PROMPT] Cannot send message - conversation in read-only mode');
        return;
      }

      // Track the start of sending a message
      this.performanceMetrics.markStart('send_message');

      let promptTextValue = this.getPromptTextValue();
      if (promptTextValue.length > 0) {
        this.promptText = '';
        this.revertToInitialHeight(this.myTextarea.nativeElement);
        this.onSendMessage.emit(promptTextValue);

        // Call the chat service directly - it will handle the span creation
        this.chatService.addUserMessage(promptTextValue);

        // Track first user message sent milestone if this is the first interaction
        if (!this.firstInteractionTracked) {
          this.firstInteractionTracked = true;
          this.performanceMetrics.trackMilestone('first_user_message_sent', {
            messageLength: promptTextValue.length
          });
        }

        // Track message sending completion
        this.performanceMetrics.markEnd('send_message', 'user_message_sent', {
          messageLength: promptTextValue.length
        });
      }
    } catch (error) {
      this.errorTracking.trackError('prompt_send_chat', error);
    }
  }

  private getPromptTextValue() {
    return this.promptText.replace(/^[ \t]+|[ \t]+$/gm, '');
  }

  ngAfterViewInit(): void {
    // Track when prompt is ready
    this.performanceMetrics.trackMilestone('prompt_component_ready');

    try {
    this.myTextarea.nativeElement.style.height = `${this.initialHeight - 20}px`; // Set initial height when textarea loads
    this.focus();
    this.setupVisualViewportHandler();

    // FIN-0105: Debug logging for mobile button centering issue
    this.logButtonPositionDebug();
    } catch (error) {
      this.errorTracking.trackError('prompt_initialization', error);
    }
  }

  private logButtonPositionDebug(): void {
    setTimeout(() => {
      const textarea = this.myTextarea.nativeElement;
      const flexContainer = textarea.parentElement;
      const button = flexContainer?.querySelector('button') as HTMLElement;

      if (textarea && flexContainer && button) {
        const flexStyle = window.getComputedStyle(flexContainer);
        const buttonStyle = window.getComputedStyle(button);

        // Get bounding rects to measure actual visual positions
        const flexRect = flexContainer.getBoundingClientRect();
        const textareaRect = textarea.getBoundingClientRect();
        const buttonRect = button.getBoundingClientRect();

        // Calculate vertical centering
        const flexCenterY = flexRect.top + flexRect.height / 2;
        const buttonCenterY = buttonRect.top + buttonRect.height / 2;
        const textareaCenterY = textareaRect.top + textareaRect.height / 2;

        console.log('[FIN-0105] Flexbox Button Position Debug:', {
          approach: 'flexbox-siblings (no absolute positioning)',
          flexContainer: {
            height: flexRect.height,
            alignItems: flexStyle.alignItems,
            display: flexStyle.display,
          },
          textarea: {
            height: textareaRect.height,
            centerY: textareaCenterY - flexRect.top,
          },
          button: {
            height: buttonRect.height,
            centerY: buttonCenterY - flexRect.top,
          },
          centering: {
            flexCenterY: flexRect.height / 2,
            buttonOffsetFromCenter: Math.round(buttonCenterY - flexCenterY),
            textareaOffsetFromCenter: Math.round(textareaCenterY - flexCenterY),
            isCentered: Math.abs(buttonCenterY - flexCenterY) < 2,
          }
        });
      }
    }, 100); // Wait for layout to settle
  }

  private scrollToBottom(smooth: boolean = false): void {
    const anchor = document.getElementById('bottom-anchor');
    if (anchor) {
      anchor.scrollIntoView({ block: 'end', behavior: smooth ? 'smooth' : 'auto' });
    }
  }

  private setupVisualViewportHandler(): void {
    // Visual Viewport API for Android keyboard handling
    if ('visualViewport' in window) {
      const vv = (window as any).visualViewport;
      const composer = this.myTextarea.nativeElement.closest('.sticky');

      if (!composer) return;

      const update = () => {
        const keyboard = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
        (composer as HTMLElement).style.transform = keyboard ? `translateY(-${keyboard}px)` : '';
        this.scrollToBottom(false); // Keep bottom visible
      };

      vv.addEventListener('resize', update);
      vv.addEventListener('scroll', update);
      update();
    }
  }

  public focus() {
    try {
      this.myTextarea.nativeElement.focus();

      // Track first interaction with the prompt
      if (!this.firstInteractionTracked) {
        this.performanceMetrics.trackMilestone('prompt_first_focus');
      }
    } catch (error) {
      this.errorTracking.trackError('prompt_focus', error);
    }
  }

  onTextareaFocus(): void {
    if (!this.firstInteractionTracked) {
      this.firstInteractionTracked = true;
      this.performanceMetrics.trackMilestone('prompt_first_focus');
    }
    // Scroll to bottom when keyboard opens
    this.scrollToBottom(false);
  }

  adjustHeight(event: any): void {
    const target = event.target as HTMLTextAreaElement;
    this.adjustTextAreaHeight(target);
  }

  private adjustTextAreaHeight(target: HTMLTextAreaElement) {
    const isMoreThanOneLine = this.isContentMoreThanOneLine(target);
    this.resetHeight(target);

    if (isMoreThanOneLine) {
      this.expandHeightToContent(target);
    } else {
      this.revertToInitialHeight(target);
    }
  }

  private resetHeight(textarea: HTMLTextAreaElement): void {
    // Set to 0 first to get accurate scrollHeight measurement
    textarea.style.height = '0';
  }

  private isContentMoreThanOneLine(textarea: HTMLTextAreaElement): boolean {
    // Single line height is approximately 24px (leading-6)
    // Use a threshold slightly above single line to detect multiline
    const singleLineThreshold = 28;
    return textarea.scrollHeight > singleLineThreshold;
  }

  private expandHeightToContent(textarea: HTMLTextAreaElement): void {
    const maxHeight = 120; // Max height to fit within pb-20 (80px) with margin
    let scrollHeight = textarea.scrollHeight;
    const constrainedHeight = Math.min(scrollHeight, maxHeight);

    textarea.style.height = `${constrainedHeight}px`;
    textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
  }

  private revertToInitialHeight(textarea: HTMLTextAreaElement): void {
    textarea.style.height = `${this.initialHeight - 20}px`;
  }

  isMobileDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  onKeyDownSendChatMessage(event: Event) {
    if (this.isMobileDevice()) {
      return;
    }
    if (event instanceof KeyboardEvent) {
      const keyboardEvent = event as KeyboardEvent;
      keyboardEvent.preventDefault();
    }
    this.sendChatMessage();
  }

  onButtonPressSendChatMessage(event: MouseEvent) {
    this.sendChatMessage();
  }
}
